import { Component, OnInit, OnDestroy, HostListener, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { FormControl, ValidatorFn, AbstractControl } from '@angular/forms';
import { LocalService, StoreService, DashboardService, HeaderPopoverService } from '../../services';
import { Subscription, Subject } from 'rxjs';
import { map, startWith, debounceTime } from 'rxjs/operators';
import { Router } from '@angular/router';

interface Region {
  id: string;
  name: string;
}

interface Country {
  id: string;
  name: string;
}

interface BeGeoOption {
  partnerNameBegeoId: string;
  [key: string]: any;
}

export function forbiddenNamesValidator(names: string[]): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!control.value) {
      return null;
    }
    const index = names.findIndex((name) => new RegExp(`^${name}$`).test(control.value));
    return index < 0 ? { forbiddenNames: { value: control.value } } : null;
  };
}

export function forbiddenBegeoIDValidator(names: BeGeoOption[]): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!control.value) {
      return null;
    }
    const index = names.findIndex((name) =>
      new RegExp(`^${name.partnerNameBegeoId}$`).test(control.value)
    );
    return index < 0 ? { forbiddenIDs: { value: control.value } } : null;
  };
}

@Component({
  selector: 'app-partner-info-selector',
  templateUrl: './partner-info-selector.component.html',
  styleUrls: ['./partner-info-selector.component.scss']
})
export class PartnerInfoSelectorComponent implements OnInit, OnDestroy {
  userData: any;
  pamUserData: any;

  regionsList: Region[] = [];
  regionName: string = '';
  regionSpinner: boolean = true;

  countryList: Country[] = [];
  selectedCountryValue: string = '';
  countrySpinnerFlag: boolean = false;

  beGeoIDoptions: BeGeoOption[] = [];
  selectedBeGeoIDValue: string = '';
  begeoidSpinnerFlag: boolean = false;

  selectedRegion: string = '';
  selectedCountry: string = '';
  selectedBgeoId: string = '';

  showBeGeoIds: boolean = false;
  isRedirecting: boolean = false;
  beGeoSearchValue: string = '';
  beGeoPage: number = 0;
  beGeoTotalElements: number = 0;

  private beGeoSearchSubject = new Subject<string>();
  private beGeoSearchSub?: Subscription;

  myControl = new FormControl();
  begeoIdControl = new FormControl();

  options: string[] = [];
  countrValue: string = '';

  @ViewChild('yourPatnerInfoPopUp') yourPatnerInfoPopUp?: ElementRef;

  private subscription?: Subscription;
  private userActivatedSubscription?: Subscription;
  private popoverSub?: Subscription;

  constructor(
    private storeService: StoreService,
    private dashboardService: DashboardService,
    private router: Router,
    private headerPopoverService: HeaderPopoverService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const userProfile = LocalService.get('userProfile');
    if (userProfile) {
      this.userData = userProfile;
      this.pamUserData = userProfile.pamDefaultSelection;
      this.initializeSelectedValues(userProfile);
    }

    this.getUserData();
    this.subscribeToUserActivation();
    this.getRegionList();
    this.beGeoSearchSub = this.beGeoSearchSubject.pipe(debounceTime(300)).subscribe((searchTerm: string) => {
      if (this.countrValue) {
        this.fetchBeGeoIds(this.countrValue, undefined, searchTerm);
      }
    });
    this.popoverSub = this.headerPopoverService.activePopover$.subscribe((name: string) => {
      if (name !== 'partner') {
        this.showBeGeoIds = false;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.userActivatedSubscription) {
      this.userActivatedSubscription.unsubscribe();
    }
    if (this.popoverSub) {
      this.popoverSub.unsubscribe();
    }
    if (this.beGeoSearchSub) {
      this.beGeoSearchSub.unsubscribe();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.showBeGeoIds) {
      return;
    }

    const target = event.target as HTMLElement;
    if (this.yourPatnerInfoPopUp && this.yourPatnerInfoPopUp.nativeElement.contains(target)) {
      return;
    }

    if (target.className === 'mat-option-text' || target.classList.contains('cdk-overlay-backdrop')) {
      return;
    }

    this.showBeGeoIds = false;
  }

  private getUserData(): void {
    this.subscription = this.storeService.getUserObj().subscribe((res: any) => {
      if (res) {
        this.userData = res;
        this.pamUserData = res.pamDefaultSelection;
        this.initializeSelectedValues(res);
      }
    });
  }

  private initializeSelectedValues(userProfile: any): void {
    if (userProfile && userProfile.userType === 'PAM' && this.pamUserData) {
      this.selectedRegion = this.pamUserData.theater || '';
      this.selectedCountry = this.pamUserData.countryGroup || '';
      if (this.pamUserData.beGeoIds && this.pamUserData.beGeoIds.length > 0) {
        this.selectedBgeoId = this.pamUserData.beGeoIds[0].partnerNameBegeoId || '';
        this.selectedBeGeoIDValue = this.pamUserData.beGeoIds[0].partnerNameBegeoId || '';
      }
    }
  }

  get isPamUserActive(): boolean {
    return this.userData && this.userData.active && this.userData.hasOwnProperty('userType') && this.userData.userType === 'PAM';
  }

  private subscribeToUserActivation(): void {
    this.userActivatedSubscription = this.storeService.userActivated.subscribe(() => {
      if (this.userData && this.userData.userType === 'PAM') {
        this.pamUserData = this.userData.pamDefaultSelection;
        this.selectedRegion = this.userData.pamDefaultSelection.theater;
        this.selectedCountry = this.userData.pamDefaultSelection.countryGroup;
        this.selectedBgeoId = this.userData.pamDefaultSelection.beGeoIds[0].partnerNameBegeoId;
      }
    });
  }

  toggleBeGeoIds(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.showBeGeoIds = !this.showBeGeoIds;
    if (this.showBeGeoIds) {
      this.headerPopoverService.open('partner');
      this.getRegionList();
    }
  }

  getRegionList(): void {
    this.myControl.disable();
    this.dashboardService
      .getRegionList(this.userData.username)
      .subscribe((data: any) => {
        this.regionsList = data.theaters.map((theater: string) => ({
          id: theater,
          name: theater
        }));
        this.regionSpinner = false;
        this.regionName = this.pamUserData && this.pamUserData.theater ? this.pamUserData.theater : '';
        if (this.regionName) {
          this.getCountryList('Rdata');
        }
      });
  }

  getSelectedRegion(): Region | null {
    if (!this.regionName || !this.regionsList.length) {
      return null;
    }
    return this.regionsList.find((region) => region.name === this.regionName) || null;
  }

  onRegionChange(selectedRegion: Region): void {
    this.regionName = selectedRegion.name;
    this.getCountryList();
  }

  getCountryList(val?: string): void {
    this.myControl.disable();
    this.begeoIdControl.disable();
    this.countrySpinnerFlag = true;
    this.dashboardService
      .getCountryListRequest(this.userData.username, this.regionName)
      .subscribe((data: any) => {
        this.options = data.countryGroups;
        this.countryList = this.options.map((country: string) => ({
          id: country,
          name: country
        }));
        this.myControl.setValidators(forbiddenNamesValidator(this.options));
        this.getStates();
        if (val === 'Rdata') {
          this.selectedCountryValue = this.pamUserData.countryGroup;
          this.myControl.setValue(this.pamUserData.countryGroup);
          this.onChangeCountry(this.pamUserData.countryGroup, 'CData');
        } else {
          this.myControl.enable();
          this.myControl.setValue('');
          this.selectedCountryValue = '';
          this.begeoIdControl.disable();
          this.begeoIdControl.setValue('');
        }
      });
  }

  getSelectedCountry(): Country | null {
    if (!this.selectedCountryValue || !this.countryList.length) {
      return null;
    }
    return this.countryList.find((country) => country.name === this.selectedCountryValue) || null;
  }

  onCountryChange(selectedCountry: Country): void {
    this.selectedCountryValue = selectedCountry.name;
    this.onChangeCountry(selectedCountry.name);
  }

  private getStates(): void {
    this.myControl.valueChanges.pipe(
      startWith(''),
      map((value: string) => (typeof value === 'string' ? value : '')),
      map((name: string) => (name ? this._filter(name) : this.options.slice()))
    ).subscribe();
    this.countrySpinnerFlag = false;
    this.myControl.enable();
  }

  private _filter(name: string): string[] {
    const filterValue = name.toLowerCase();
    return this.options.filter((option) => {
      const optionLower = option.toLowerCase();
      return (optionLower.includes(filterValue) && optionLower !== filterValue) ||
             this.options.indexOf(filterValue.toUpperCase()) > -1;
    });
  }

  onChangeCountry(value: string, val?: string): void {
    this.beGeoSearchValue = '';
    this.fetchBeGeoIds(value, val);
  }

  onBeGeoSearchInput(value: string): void {
    this.beGeoSearchValue = value;
    this.beGeoSearchSubject.next(value);
  }

  onBeGeoScrollEnd(): void {
    if (this.begeoidSpinnerFlag) { return; }
    if (this.beGeoIDoptions.length >= this.beGeoTotalElements) { return; }
    this.beGeoPage++;
    this.fetchBeGeoIds(this.countrValue, undefined, this.beGeoSearchValue || undefined, true);
  }

  private fetchBeGeoIds(value: string, val?: string, search?: string, append?: boolean): void {
    if (!append) {
      this.beGeoPage = 0;
      if (!val) {
        this.beGeoIDoptions = [];
      }
    }
    this.begeoidSpinnerFlag = true;
    this.countrValue = value;
    const paramReq = {
      username: this.userData.username,
      countries: [value]
    };
    this.dashboardService
      .getBeGeoIdsRequest(paramReq, search, 10, this.beGeoPage)
      .subscribe((data: any) => {
        this.beGeoTotalElements = data.totalElements || 0;
        if (append) {
          this.beGeoIDoptions = this.beGeoIDoptions.concat(data.content || []);
        } else {
          this.beGeoIDoptions = data.content || [];
        }
        this.begeoIdControl.setValidators(forbiddenBegeoIDValidator(this.beGeoIDoptions));
        this.getBegeoId();
        if (val === 'CData') {
          const defaultBeGeoID = this.pamUserData.beGeoIds[0].partnerNameBegeoId;
          this.selectedBeGeoIDValue = defaultBeGeoID;
          this.begeoIdControl.setValue(defaultBeGeoID);
        } else if (!search && !append) {
          this.selectedBeGeoIDValue = '';
          this.begeoIdControl.setValue('');
          this.begeoIdControl.enable();
        }
      });
  }

  getSelectedBeGeoID(): BeGeoOption | null {
    if (!this.selectedBeGeoIDValue || !this.beGeoIDoptions.length) {
      return null;
    }
    return this.beGeoIDoptions.find((item) => item.partnerNameBegeoId === this.selectedBeGeoIDValue) || null;
  }

  onBeGeoIDChange(selectedBeGeoID: BeGeoOption): void {
    this.showBeGeoIds = false;
    this.selectedBeGeoIDValue = selectedBeGeoID.partnerNameBegeoId;
    this.changeCompanyId(
      selectedBeGeoID.companyId,
      selectedBeGeoID.beGeoId,
      selectedBeGeoID.partnerName,
      selectedBeGeoID.region,
      selectedBeGeoID.companyEligibleChallengesDTO,
      selectedBeGeoID.partnerNameBegeoId
    );
  }

  private getBegeoId(): void {
    this.begeoIdControl.valueChanges.pipe(
      startWith(''),
      map((value: string) => (typeof value === 'string' ? value : '')),
      map((name: string) => (name ? this._GeoIDfilter(name) : this.beGeoIDoptions.slice()))
    ).subscribe();
    this.begeoIdControl.enable();
    this.begeoidSpinnerFlag = false;
  }

  private _GeoIDfilter(value: string): BeGeoOption[] {
    const filterValue = value.toLowerCase().split('-')[0];
    return this.beGeoIDoptions.filter((option) =>
      option.partnerNameBegeoId.toLowerCase().includes(filterValue)
    );
  }

  private changeCompanyId(
    compId: string,
    geoId: string,
    name: string,
    regionNm: string,
    compDto: any,
    selectedBegeo: string
  ): void {
    this.isRedirecting = true;
    this.cdr.detectChanges();
    this.showBeGeoIds = false;
    this.selectedCountry = this.myControl.value;
    this.selectedRegion = this.regionName;
    this.selectedBgeoId = selectedBegeo;

    this.userData.company.id = compId;
    this.userData.company.region = regionNm;
    if (this.userData.pamDefaultSelection.beGeoIds && this.userData.pamDefaultSelection.beGeoIds.length > 0) {
      this.userData.pamDefaultSelection.beGeoIds[0].companyEligibleChallengesDTO = compDto;
    }

    this.pamUserData.theater = this.regionName;
    this.pamUserData.countryGroup = this.countrValue;

    const beGeoIds = this.pamUserData.beGeoIds ||
      (this.pamUserData.pamBeGeoIdsDTO && this.pamUserData.pamBeGeoIdsDTO.beGeoIds) || [];
    if (beGeoIds.length > 0) {
      beGeoIds[0].beGeoId = geoId;
      beGeoIds[0].partnerName = name;
      beGeoIds[0].partnerNameBegeoId = selectedBegeo;
    }

    const pamDataToSave: any = {
      theater: this.regionName,
      countryGroup: this.countrValue,
      pamBeGeoIdsDTO: { beGeoIds: beGeoIds }
    };

    LocalService.set('userProfile', this.userData);
    LocalService.set('pamData', pamDataToSave);
    setTimeout(() => this.redirectTo('home/manager/manage-challenges'), 50);
  }

  private redirectTo(uri: string): void {
    const reuseStrategy = this.router.routeReuseStrategy;
    const prevShouldReuse = reuseStrategy.shouldReuseRoute.bind(reuseStrategy);
    reuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';
    this.router.navigate([uri]).then(() => {
      this.isRedirecting = false;
      reuseStrategy.shouldReuseRoute = prevShouldReuse;
      this.router.onSameUrlNavigation = 'ignore';
    });
  }
}
